import { useQuery } from '@tanstack/react-query'
import { useTRPC } from './trpc'

/**
 * Pool custody: a web account's SOL, USDC and USDT live in Sonar's pool with
 * its other tokens (John, Oct 30 2026). Every flow then goes through the
 * pool: deposits land in it, buys are paid from it, sales and withdrawals
 * come out of it. Read once per session; false until known.
 */
export function usePoolMode(): boolean {
  const trpc = useTRPC()
  const config = useQuery({ ...trpc.vault.config.queryOptions(), staleTime: 5 * 60_000 })
  return config.data?.pool ?? false
}

/** the places an account's money has lived: the exchanges, and Sonar for the records made under it (retired Nov 3 2026) */
export type Custody = 'sonar' | 'binanceus' | 'mexc' | 'coinbase'
export type ExchangeId = 'binanceus' | 'mexc' | 'coinbase'

/** the exchanges, in the order the chips cycle through them */
export const EXCHANGE_IDS: readonly ExchangeId[] = ['mexc', 'binanceus', 'coinbase']

export function isExchangeId(custody: string | null | undefined): custody is ExchangeId {
  return custody === 'binanceus' || custody === 'mexc' || custody === 'coinbase'
}

/** Which exchange the account is on and which ones this deployment offers (John, Oct 30 2026: a toggle per account; Sonar retired Nov 3 2026). */
export function useCustody(): { custody: ExchangeId; exchange: boolean; dispenceVault: boolean; custodies: Record<ExchangeId, boolean> } {
  const trpc = useTRPC()
  const config = useQuery({ ...trpc.vault.config.queryOptions(), staleTime: 5 * 60_000 })
  return {
    custody: config.data?.custody ?? 'mexc',
    exchange: config.data?.exchange ?? false,
    /** the account's tokens and fee SOL live in its own Dispence vault; its address takes SOL or any token */
    dispenceVault: config.data?.dispenceVault ?? false,
    custodies: config.data?.custodies ?? { binanceus: false, mexc: false, coinbase: false },
  }
}

export const CUSTODY_LABELS: Record<Custody, string> = { sonar: 'Sonar', binanceus: 'Binance.US', mexc: 'MEXC', coinbase: 'Coinbase' }

/**
 * Kraken is wired up (an API key with query and deposit permissions): the withdraw dialog can fill a fresh Kraken address.
 * `known` is false while the answer is still on its way, for a list that must not render before it has every source.
 */
export function useKrakenAvailability(): { known: boolean; available: boolean } {
  const trpc = useTRPC()
  const config = useQuery({ ...trpc.vault.config.queryOptions(), staleTime: 5 * 60_000 })
  return { known: !config.isPending, available: config.data?.kraken ?? false }
}

export function useKrakenAvailable(): boolean {
  return useKrakenAvailability().available
}
